import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Loader2, Check, X, KeyRound, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// 2026-02-19 (Dave) · Self-service Forgot password? flow
// ---------------------------------------------------------------------------
// Two modals, one file:
//   * <ForgotPasswordModal> — opened from the Sign-In screen. Collects an
//     email, POSTs /auth/forgot-password, always shows the same generic
//     success state (enumeration-safe). Never blocks a repeat submission.
//   * <ResetPasswordTokenModal> — opened when the URL hash is
//     `#reset?token=<uuid>`. Collects new + confirm password, POSTs
//     /auth/reset-password-token, then redirects to `#login` on success.
// Both use the canonical ConfirmDialog spec (navy tint backdrop 55 %,
// 2-px navy inner border, 8-px rounded corners).
// ---------------------------------------------------------------------------

function BackdropShell({ children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(15,42,68,0.55)" }}
      data-testid={testId}
    >
      <div
        className="bg-white border-2 border-navy shadow-2xl w-[440px] max-w-full rounded-[8px]"
        style={{ borderRadius: 8, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ---- Forgot-password (request a link) -------------------------------------
export function ForgotPasswordModal({ initialEmail = "", onClose }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const cleaned = (email || "").trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) {
      setErr("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: cleaned });
      setSent(true);
    } catch (ex) {
      // Endpoint is enumeration-safe (always 200) — a failure here means
      // network / server error only.
      setErr(ex?.response?.data?.detail || ex.message || "Could not send the reset email. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BackdropShell testId="forgot-password-modal">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash/40">
        <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">
          {sent ? "Check Your Inbox" : "Forgot Password"}
        </div>
        <button onClick={onClose} className="text-slate hover:text-navy" data-testid="forgot-password-close">
          <X size={16} />
        </button>
      </div>
      {sent ? (
        <div className="p-5">
          <div className="flex items-start gap-2 border border-navy bg-navy-wash/40 px-3 py-2 rounded-[3px] text-[12px] text-navy leading-snug mb-3">
            <Mail size={14} className="mt-0.5 shrink-0 text-navy" />
            <div>
              If an account exists for <span className="font-semibold">{email}</span>, a reset link has been emailed. Check your inbox (and spam) within a few minutes. The link is good for 60 minutes and works only once.
            </div>
          </div>
          <p className="text-[11px] text-slate leading-relaxed">
            Didn&apos;t get it? Wait a minute, then request another. If the address isn&apos;t recognized, no email is sent — that&apos;s intentional.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B]"
              data-testid="forgot-password-done"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="p-4 flex flex-col gap-3">
            <p className="text-[11.5px] text-ink leading-relaxed">
              Enter the email address on your uReserve account. If we recognize it, we&apos;ll email a link you can use to choose a new password.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">Email</span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border border-rule rounded-[3px] px-2.5 py-2 text-[13px] focus:outline-none focus:border-navy"
                data-testid="forgot-password-email"
                autoFocus
                disabled={busy}
              />
            </label>
            {err && (
              <div className="text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2]" data-testid="forgot-password-error">
                {err}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 px-4 py-2.5 border-t border-rule bg-navy-wash/40">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
              data-testid="forgot-password-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-password-submit"
              className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40 flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
              {busy ? "Sending…" : "Send Reset Link"}
            </button>
          </div>
        </form>
      )}
    </BackdropShell>
  );
}

// ---- Reset with token -----------------------------------------------------
function readResetToken() {
  const h = typeof window !== "undefined" ? window.location.hash || "" : "";
  if (!h.startsWith("#reset")) return "";
  const qi = h.indexOf("?");
  if (qi < 0) return "";
  const params = new URLSearchParams(h.slice(qi + 1));
  return (params.get("token") || "").trim();
}

export function ResetPasswordTokenModal({ onClose }) {
  const token = readResetToken();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [doneEmail, setDoneEmail] = useState("");
  const pwRef = React.useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("This reset link is missing its token. Request a new one from the Sign-in page."); return; }
    if (pw.length < 12) { setErr("New password must be at least 12 characters."); return; }
    if (pw !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password-token`, {
        token, new_password: pw,
      });
      setDoneEmail(res?.data?.email || "");
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Could not reset your password. Request a new reset link.");
    } finally {
      setBusy(false);
    }
  };

  const goToLogin = () => {
    // Clear the hash + call onClose so AppGate re-renders LoginScreen.
    try {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#login`);
    } catch { /* ignore */ }
    onClose && onClose();
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  useEffect(() => {
    if (!done) return undefined;
    // Auto-redirect after 3s so the user isn't stuck on the success screen.
    const t = setTimeout(goToLogin, 3000);
    return () => clearTimeout(t);
  }, [done]);

  return (
    <BackdropShell testId="reset-password-token-modal">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash/40">
        <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">
          {done ? "Password Reset" : "Choose a New Password"}
        </div>
      </div>
      {done ? (
        <div className="p-5">
          <div className="flex items-start gap-2 border border-navy bg-navy-wash/40 px-3 py-2 rounded-[3px] text-[12px] text-navy leading-snug mb-3">
            <Check size={14} className="mt-0.5 shrink-0 text-navy" />
            <div>
              Your password for <span className="font-semibold">{doneEmail || "your account"}</span> has been reset. Sign in with your new password below.
            </div>
          </div>
          <p className="text-[11px] text-slate leading-relaxed">Redirecting to Sign-in in a few seconds…</p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={goToLogin}
              className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B]"
              data-testid="reset-token-done"
            >
              Go to Sign-in
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="p-4 flex flex-col gap-3">
            <p className="text-[11.5px] text-ink leading-relaxed">
              Choose a strong new password. It must be at least 12 characters. All other devices signed into this account will be signed out.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">New password</span>
              <div className="relative">
                <input
                  ref={pwRef}
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full border border-rule rounded-[3px] px-2.5 py-2 pr-10 text-[13px] focus:outline-none focus:border-navy"
                  data-testid="reset-token-new-pw"
                  autoFocus
                  disabled={busy}
                />
                <button
                  type="button"
                  onClick={() => {
                    // 2026-02-19 (Dave) — park cursor at start of the
                    // field after toggling visibility. See LoginScreen
                    // for rationale.
                    setShowPw((v) => !v);
                    requestAnimationFrame(() => {
                      const el = pwRef.current;
                      if (!el) return;
                      try {
                        el.focus();
                        el.setSelectionRange(0, 0);
                        el.scrollLeft = 0;
                      } catch { /* ignore */ }
                    });
                  }}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-slate hover:text-navy"
                  data-testid="reset-token-toggle-pw"
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">Confirm password</span>
              <input
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="border border-rule rounded-[3px] px-2.5 py-2 text-[13px] focus:outline-none focus:border-navy"
                data-testid="reset-token-confirm-pw"
                disabled={busy}
              />
            </label>
            {err && (
              <div className="text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2]" data-testid="reset-token-error">
                {err}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 px-4 py-2.5 border-t border-rule bg-navy-wash/40">
            <button
              type="button"
              onClick={goToLogin}
              disabled={busy}
              className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
              data-testid="reset-token-cancel"
            >
              Back to Sign-in
            </button>
            <button
              type="submit"
              disabled={busy || !token}
              data-testid="reset-token-submit"
              className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40 flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
              {busy ? "Setting…" : "Set New Password"}
            </button>
          </div>
        </form>
      )}
    </BackdropShell>
  );
}
